import React, { type FunctionComponent, useEffect, useState } from 'react'
import { logError } from '@tomra/datadog-browser-logging'
import { CharityActionListItemSkeleton } from '../common/CharityActionListItemSkeleton'
import { ActionListItem } from '../common/ActionListItem'
import { loadNewCharityActions, updateCharityNote } from '../../../../services/charity.service'
import { HeartIcon, QuestionMarkIcon } from '../../../icons'
import { AddNote } from './AddNote'
import { mergeCharityAndCampaignData } from '../../../../lib/charity'

type CharityActionItem = CharitySummaryType & { campaigns: CampaignSummaryType[] }

type Props = {
  appContext: CharityAppContext
}

export const CharityActionList: FunctionComponent<Props> = ({ appContext }) => {
  const [newActions, setNewActions] = useState({
    charities: [],
    campaigns: [],
    pendingCharities: [],
    pendingCampaigns: []
  })
  const [mergedActions, setMergedActions] = useState<CharityActionItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    const { runAll, abortAll } = loadNewCharityActions()

    runAll()
      .then(actions => {
        setNewActions(actions)
        setIsLoading(false)
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          logError(new Error('Unable to load charity actions'), error)
          setIsLoading(false)
        }
      })

    return () => abortAll()
  }, [])

  useEffect(() => {
    if (newActions.charities.length > 0) {
      const merged = mergeCharityAndCampaignData(
        newActions.charities,
        newActions.pendingCharities,
        newActions.campaigns,
        newActions.pendingCampaigns
      )

      setMergedActions(merged)
    }
  }, [newActions])

  const filterByState = (action: CharityActionItem) => {
    return (action as CharitySummaryType).appContext === appContext
  }

  const filterByStatus = (action: CharityActionItem) => {
    return (action as CharitySummaryType).status !== 'DISABLED'
  }

  const updateNote = async (charityId: string, note: string) => {
    await updateCharityNote(charityId, note)
    const updated = mergedActions.map(action => (action.id === charityId ? { ...action, note } : action))
    setMergedActions(updated)
  }

  const filteredCharities = mergedActions.filter(filterByState).filter(filterByStatus)

  return isLoading ? (
    <div className="card [&>*:nth-child(even)]:bg-grey-light [&>*:first-child]:pt-md [&>*:last-child]:pb-md">
      <CharityActionListItemSkeleton count={10} />
    </div>
  ) : mergedActions.length === 0 ? (
    <div className="flex flex-col items-center space-y-md">
      <div className="bg-white rounded w-10 h-10 flex items-center justify-center">
        <HeartIcon width="4rem" height="4rem" className="text-storm" />
      </div>
      <div>
        <h2 className="text-lg text-center">All done!</h2>
        <p className="text-center">No charities or campaigns to review.</p>
      </div>
    </div>
  ) : (
    <>
      {filteredCharities.length === 0 && (
        <div className="flex flex-col items-center space-y-md">
          <div className="bg-white rounded w-10 h-10 flex items-center justify-center">
            <QuestionMarkIcon width="4rem" height="4rem" className="text-storm" />
          </div>
          <div>
            <h2 className="text-lg text-center">Nothing to show here</h2>
            <p className="text-center">No charities or campaigns matches your filter.</p>
          </div>
        </div>
      )}

      <ul className="w-full card [&>*:nth-child(even)]:bg-grey-light [&>*:first-child]:pt-md [&>*:last-child]:pb-md">
        {filteredCharities.map((charity, i) => {
          return (
            <ActionListItem
              key={`pendingCharity-${i}`}
              AddNote={<AddNote charity={charity} updateNote={updateNote} />}
              charity={charity}
            />
          )
        })}
      </ul>
    </>
  )
}
